"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { hideTip, showTip, type Tip } from "@/lib/menu/Tooltip";

const SIZES = {
  xs: "h-6 w-6 rounded-md",
  sm: "h-8 w-8 rounded-lg",
  lg: "h-9 w-9 rounded-xl",
} as const;

export function IconButton({
  label,
  size = "sm",
  danger = false,
  flat = false,
  tip,
  className,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
  onClick,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  size?: keyof typeof SIZES;
  /* muted/deafened look — translucent red wash + red glyph */
  danger?: boolean;
  /* inside a split group — no own hover/active bg, the wrapper carries it */
  flat?: boolean;
  /* tooltip — string for small, {icon,title,hint} mid, +description large */
  tip?: Tip;
}) {
  return (
    <button
      type="button"
      /* no native title when a lib tooltip is set — they'd double up */
      title={tip ? undefined : label}
      aria-label={label}
      className={`grid shrink-0 place-items-center transition duration-200 active:scale-90 ${SIZES[size]} ${
        flat
          ? danger
            ? "text-red-400 hover:text-red-300"
            : "text-fg-mute hover:text-fg"
          : danger
            ? "bg-red-500/15 text-red-400 hover:bg-red-500/25 hover:text-red-300"
            : "text-fg-mute hover:bg-lift hover:text-fg"
      } ${className ?? ""}`}
      onPointerEnter={(e) => {
        if (tip) showTip(tip, e.currentTarget);
        onPointerEnter?.(e);
      }}
      onPointerLeave={(e) => {
        if (tip) hideTip();
        onPointerLeave?.(e);
      }}
      onFocus={(e) => {
        if (tip) showTip(tip, e.currentTarget);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        if (tip) hideTip();
        onBlur?.(e);
      }}
      onClick={(e) => {
        if (tip) hideTip();
        onClick?.(e);
      }}
      {...props}
    />
  );
}

export function SectionLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`px-5 pt-5 pb-2 text-micro font-semibold tracking-[0.14em] text-fg-faint uppercase ${
        className ?? ""
      }`}
    >
      {children}
    </p>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span aria-hidden className={`block bg-lift ${className ?? ""}`} />
  );
}


